import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, ListTodo, Trash2, ReceiptText, PhoneCall, Users, CalendarClock, CalendarPlus, Check, Pencil, StickyNote, HelpCircle, X, Package } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import VoiceButton from "@/components/VoiceButton";
import AskQuestionsDialog from "@/components/AskQuestionsDialog";
import AdminHeader from "@/components/AdminHeader";

const inputCls = "w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";
const KINDS = [
  { v: "general", label: "General job" },
  { v: "raise_invoice", label: "Raise an invoice" },
  { v: "quote", label: "Raise a quote" },
  { v: "booking", label: "Quote request" },
  { v: "email", label: "Send an email" },
  { v: "chase_invoice", label: "Chase an invoice" },
  { v: "call", label: "Phone call" },
  { v: "meeting", label: "Meeting" },
  { v: "other", label: "Other" },
];
const KIND_STYLE = {
  raise_invoice: "bg-sky-50 text-[#005EB8]",
  quote: "bg-sky-50 text-[#005EB8]",
  booking: "bg-violet-50 text-violet-700",
  email: "bg-sky-50 text-[#005EB8]",
  chase_invoice: "bg-amber-50 text-[#ED8B00]",
  call: "bg-emerald-50 text-[#007F3B]",
  meeting: "bg-violet-50 text-violet-700",
  general: "bg-slate-100 text-slate-600",
  other: "bg-slate-100 text-slate-600",
};

const defaultDeadline = () => {
  const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return { due_date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
           due_time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
};

const smartActions = (t, clients) => {
  const text = (t.title || "").toLowerCase();
  const acts = [];
  if (t.kind === "raise_invoice" || t.kind === "chase_invoice" || /\binvoice\b/.test(text))
    acts.push({ key: "invoice", label: t.kind === "chase_invoice" ? "Chase it" : "Invoice", to: "/admin/invoices" });
  if (t.kind === "quote" || /\bquote\b/.test(text))
    acts.push({ key: "quote", label: "Quote", to: "/admin/invoices" });
  if (/\b(add|new)\s+(a\s+)?client\b/.test(text))
    acts.push({ key: "client", label: "Add client", to: "/admin/contacts" });
  if (t.email_uid)
    acts.push({ key: "open-email", label: "Open email", to: `/admin/emails?uid=${t.email_uid}&folder=${encodeURIComponent(t.email_folder || "INBOX")}` });
  if (t.kind === "email")
    acts.push({ key: "send-email", label: "Send email", to: `/admin/emails?compose=1&to=${encodeURIComponent(t.contact_email || "")}&subject=${encodeURIComponent(t.title)}` });
  if (t.client_id)
    acts.push({ key: "invoices", label: "View invoices", to: `/admin/invoices?client=${t.client_id}` });
  if (t.kind === "booking" && t.client_id) {
    acts.push({ key: "quote", label: "Raise quote", to: `/admin/invoices?new=quote&client=${t.client_id}` });
    acts.push({ key: "invoice", label: "Raise invoice", to: `/admin/invoices?new=invoice&client=${t.client_id}` });
  }
  if (t.kind === "call" || /\b(call|ring|phone)\b/.test(text)) {
    const c = clients.find((x) => x.id === t.client_id);
    const ct = (c?.contacts || []).find((x) => x.phone);
    const phone = (ct?.phone || c?.phone || "").replace(/[\s()-]/g, "");
    acts.push({ key: "call", label: t.contact_name ? `Call ${t.contact_name}` : "Call", phone, to: "/admin/contacts" });
  }
  return acts;
};

export default function AdminJobs() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState(null);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(() => ({ title: "", kind: "general", client_id: "", contact_name: "", contact_email: "", ...defaultDeadline() }));
  const [editing, setEditing] = useState(null);
  const [qforms, setQforms] = useState([]);
  const [events, setEvents] = useState([]);
  const [askFor, setAskFor] = useState(null);
  const [viewForm, setViewForm] = useState(null);

  const [notesOpen, setNotesOpen] = useState(null);
  const [noteText, setNoteText] = useState("");

  const addNote = async (t) => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/crm/tasks/${t.id}/notes`, { text: noteText });
      setNoteText("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const startEdit = (t) => {
    setEditing({ id: t.id, title: t.title, kind: t.kind, client_id: t.client_id || "",
      contact_name: t.contact_name || "", contact_email: t.contact_email || "",
      due_date: t.due_date || "", due_time: t.due_time || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/crm/tasks/${editing.id}`, {
        title: editing.title, kind: editing.kind, client_id: editing.client_id,
        contact_name: editing.contact_name, contact_email: editing.contact_email,
        due_date: editing.due_date, due_time: editing.due_time,
      });
      toast.success("Job updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const [filter, setFilter] = useState("all");
  const [saving, setSaving] = useState(false);
  const todayStr = new Date().toISOString().slice(0, 10);
  const weekStr = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const nowD = new Date();
  const nowTime = `${String(nowD.getHours()).padStart(2, "0")}:${String(nowD.getMinutes()).padStart(2, "0")}`;
  const isOverdue = useCallback((t) => t.due_date && !t.done &&
    (t.due_date < todayStr || (t.due_date === todayStr && t.due_time && t.due_time <= nowTime)), [todayStr, nowTime]);

  const load = useCallback(async () => {
    try {
      const [{ data: t }, { data: c }, { data: qf }, { data: ev }] = await Promise.all([
        api.get("/crm/tasks"), api.get("/clients"), api.get("/question-forms"), api.get("/events")]);
      setTasks(t);
      setClients(c);
      setQforms(qf);
      setEvents(ev);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/crm/tasks", form);
      toast.success("To-do added");
      setForm({ title: "", kind: "general", client_id: "", contact_name: "", contact_email: "", ...defaultDeadline() });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (task) => {
    try {
      await api.patch(`/crm/tasks/${task.id}`, { done: !task.done });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (task) => {
    if (!window.confirm(`Delete job "${task.title}"?`)) return;
    try {
      await api.delete(`/crm/tasks/${task.id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const acceptStaffTask = async (t) => {
    try {
      const { data } = await api.post(`/staff-directory/${encodeURIComponent(t.staff_key)}/accept`);
      toast.success(data.message);
      if (data.temp_password) window.alert(`Email could not be sent — give them this temporary password: ${data.temp_password}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const declineStaffTask = async (t) => {
    if (!window.confirm(`Decline ${t.contact_name || "this sign-up"}? They'll be removed from the staff pool.`)) return;
    try {
      const { data } = await api.delete(`/staff-directory/${encodeURIComponent(t.staff_key)}`);
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toCalendar = async (task) => {
    try {
      const { data } = await api.post(`/crm/tasks/${task.id}/to-calendar`);
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const attachEvent = async (task, eid) => {
    try {
      const { data } = await api.put(`/crm/tasks/${task.id}/event`, { event_id: eid });
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const { outstanding, done, dueToday, dueWeek, overdueList, doneWeek } = useMemo(() => {
    const out = (tasks || []).filter((t) => !t.done);
    const dn = (tasks || []).filter((t) => t.done);
    return {
      outstanding: out,
      done: dn,
      dueToday: out.filter((t) => t.due_date === todayStr),
      dueWeek: out.filter((t) => t.due_date && t.due_date <= weekStr),
      overdueList: out.filter(isOverdue),
      doneWeek: dn.filter((t) => t.done_at && new Date(t.done_at).getTime() >= weekAgo),
    };
  }, [tasks, todayStr, weekStr, weekAgo, isOverdue]);
  const shown = filter === "overdue" ? overdueList
    : filter === "today" ? dueToday
    : filter === "week" ? dueWeek
    : outstanding;

  const qformsByTask = useMemo(() => {
    const m = {};
    (qforms || []).forEach((f) => { if (f.task_id) (m[f.task_id] = m[f.task_id] || []).push(f); });
    return m;
  }, [qforms]);

  const linkFormEvent = async (f, eid) => {
    if (!eid) return;
    try {
      const { data } = await api.post(`/question-forms/${f.id}/link-event`, { event_id: eid });
      toast.success(data.message);
      setViewForm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const statCard = (key, label, count, activeCls, testid) => (
    <button key={key} onClick={() => setFilter((f) => (f === key ? "all" : key))} data-testid={testid}
      className={`flex-1 min-w-[104px] rounded-xl border p-3 text-left transition-colors ${filter === key ? activeCls : "bg-white border-slate-200 hover:border-[#005EB8]"}`}>
      <span className="block font-heading text-2xl font-bold leading-none">{count}</span>
      <span className="block text-[10px] font-bold uppercase tracking-wide mt-1 opacity-80">{label}</span>
    </button>
  );

  const renderTask = (t) => {
    const overdue = isOverdue(t);
    return (
      <div key={t.id} data-testid={`job-row-${t.id}`}
        className={`flex flex-wrap items-start gap-3 bg-white rounded-xl border p-3.5 ${t.done ? "border-slate-100 opacity-60" : overdue ? "border-red-200" : "border-slate-200"}`}>
        <input type="checkbox" checked={t.done} onChange={() => toggle(t)} data-testid={`job-check-${t.id}`}
          className="mt-1 h-5 w-5 accent-[#005EB8] cursor-pointer shrink-0" />
        <div className="flex-1 min-w-[160px]">
          <p className={`text-sm font-semibold text-[#0F2547] ${t.done ? "line-through" : ""}`}>{t.title}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${KIND_STYLE[t.kind] || KIND_STYLE.general}`}>
              {KINDS.find((k) => k.v === t.kind)?.label || t.kind}
            </span>
            <button onClick={() => setNotesOpen(notesOpen === t.id ? null : t.id)} data-testid={`job-notes-toggle-${t.id}`}
              className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 hover:bg-sky-50 hover:text-[#005EB8] inline-flex items-center gap-1 transition-colors">
              <StickyNote className="h-3 w-3" /> Notes{(t.notes || []).length ? ` (${t.notes.length})` : ""}
            </button>
            {(qformsByTask[t.id] || []).map((f) => (
              <button key={f.id} onClick={() => setViewForm(f)} data-testid={`job-qform-${f.id}`}
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 transition-colors ${f.status === "answered" ? "bg-emerald-50 text-[#007F3B] hover:bg-emerald-100" : "bg-amber-50 text-[#ED8B00] hover:bg-amber-100"}`}>
                <HelpCircle className="h-3 w-3" /> {f.status === "answered" ? "Answers in — view" : "Questions sent — awaiting reply"}
              </button>
            ))}
            {t.client_name && <span className="text-[11px] text-slate-500 font-semibold">{t.client_name}{t.contact_name ? ` · ${t.contact_name}` : ""}</span>}
            {t.due_date && (
              <span className={`text-[11px] font-bold inline-flex items-center gap-1 ${overdue ? "text-[#D5281B]" : "text-slate-500"}`}>
                <CalendarClock className="h-3 w-3" />
                {overdue ? "Overdue — was due " : "Due by "}{new Date(`${t.due_date}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}{t.due_time ? ` ${t.due_time}` : ""}
              </span>
            )}
            {t.done && t.done_at && (
              <span className="text-[11px] text-emerald-600 font-semibold">Done {new Date(t.done_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
            )}
          </div>
          {notesOpen === t.id && (
            <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2.5" data-testid={`job-notes-${t.id}`}>
              {(t.notes || []).length === 0 && <p className="text-[11px] text-slate-400 mb-1.5">No notes yet — add updates, extra people, anything to remember.</p>}
              {(t.notes || []).map((n, i) => (
                <p key={i} className="text-[11px] text-slate-600 mb-1" data-testid={`job-note-${t.id}-${i}`}>
                  {n.text} <span className="text-slate-400">— {n.by} · {(n.at || "").slice(0, 10)}</span>
                </p>
              ))}
              <div className="flex gap-1.5 mt-1.5">
                <input value={noteText} onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add info, updates, extra people…" data-testid={`job-note-input-${t.id}`}
                  className="flex-1 h-9 rounded-lg border border-slate-300 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                <button onClick={() => addNote(t)} disabled={!noteText.trim()} data-testid={`job-note-add-${t.id}`}
                  className="h-9 px-3 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-40 text-white text-[11px] font-bold transition-colors">Add</button>
              </div>
            </div>
          )}
        </div>
        {t.staff_key && !t.done && (
          <div className="mt-1.5 mb-2 w-full flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-testid={`job-staff-signup-${t.id}`}>
            <p className="text-[11px] font-semibold text-amber-800 flex-1 min-w-[200px]">New staff member waiting — accept to create their login so they appear in Staff Management and can be sent shifts, or decline to remove them.</p>
            <button onClick={() => acceptStaffTask(t)} data-testid={`job-accept-staff-${t.id}`}
              className="h-9 px-4 rounded-lg bg-[#007F3B] hover:bg-[#006331] text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
              <Check className="h-3.5 w-3.5" /> Accept — create their login
            </button>
            <button onClick={() => declineStaffTask(t)} data-testid={`job-decline-staff-${t.id}`}
              className="h-9 px-4 rounded-lg border-2 border-red-200 text-[#D5281B] hover:bg-red-50 text-xs font-bold inline-flex items-center gap-1.5 transition-colors bg-white">
              <X className="h-3.5 w-3.5" /> Decline — remove them
            </button>
          </div>
        )}
        {t.kind === "booking" && t.booking && (
          <div className="mt-1.5 mb-2 rounded-lg bg-violet-50 border border-violet-100 p-2.5 text-[11px] text-slate-600" data-testid={`job-booking-${t.id}`}>
            <p className="font-bold text-violet-800">
              {[t.booking.date_of_service, [t.booking.start_time, t.booking.end_time].filter(Boolean).join("–"), t.booking.service_location].filter(Boolean).join(" · ")}
            </p>
            <p>
              {[
                t.booking.participants && `${t.booking.participants} participants`,
                t.booking.max_capacity && `capacity ${t.booking.max_capacity}`,
                t.booking.sites && `${t.booking.sites} site(s)`,
                t.booking.overnight === "Yes" && "overnight cover",
                t.booking.alcohol === "Yes" && "alcohol",
                t.booking.security === "Yes" && "security",
                t.booking.parking === "Yes" && "parking",
                t.booking.freshwater === "Yes" && "fresh water",
                t.booking.power === "Yes" && "power",
              ].filter(Boolean).join(" · ")}
            </p>
            {t.booking.additional_info && <p className="whitespace-pre-wrap">{t.booking.additional_info}</p>}
            {!t.done && (
              <button onClick={async () => {
                  const b = t.booking;
                  try {
                    const { data: newEv } = await api.post("/events", {
                      name: b.company_name || `${t.client_name} event`,
                      venue: b.service_location || "", event_date: b.date_of_service || "",
                      times: [b.start_time, b.end_time].filter(Boolean).join("–"),
                      expected_attendance: Number(b.participants) || Number(b.max_capacity) || null,
                    });
                    toast.success("Event added to the calendar — build their quote now");
                    navigate(`/admin/invoices?event=${newEv.id}&kind=quote`);
                  } catch (err) { toast.error(formatApiError(err)); }
                }} data-testid={`job-quote-${t.id}`}
                className="mt-2 h-9 px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                <ReceiptText className="h-3.5 w-3.5" /> Turn into quote
              </button>
            )}
          </div>
        )}
        {!t.done && smartActions(t, clients).map((a) =>
          a.key === "call" && a.phone ? (
            <a key={a.key} href={`tel:${a.phone}`} data-testid={`job-action-${a.key}-${t.id}`}
              className="h-9 px-3 rounded-lg border border-slate-300 text-[#007F3B] hover:border-[#007F3B] text-xs font-bold inline-flex items-center gap-1.5 shrink-0">
              <PhoneCall className="h-3.5 w-3.5" /> {a.label}
            </a>
          ) : (
            <button key={a.key} onClick={() => navigate(a.to)} data-testid={`job-action-${a.key}-${t.id}`}
              className="h-9 px-3 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5 shrink-0">
              {a.key === "call" ? <PhoneCall className="h-3.5 w-3.5" /> : a.key === "client" ? <Users className="h-3.5 w-3.5" /> : <ReceiptText className="h-3.5 w-3.5" />} {a.label}
            </button>
          )
        )}
        {t.kind === "booking" && !t.done && (
          <button onClick={() => setAskFor(t)} data-testid={`job-askq-${t.id}`}
            className="h-9 px-3 rounded-lg border border-slate-300 text-violet-700 hover:border-violet-500 text-xs font-bold inline-flex items-center gap-1.5 shrink-0">
            <HelpCircle className="h-3.5 w-3.5" /> Ask questions
          </button>
        )}
        {t.due_date && !t.done && (
          <button onClick={() => toCalendar(t)} data-testid={`job-calendar-btn-${t.id}`}
            title={t.event_id ? "Move it to the job's date on the Calendar" : "Add this to the Calendar on its date"}
            className="h-9 px-3 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5 shrink-0">
            <CalendarPlus className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{t.event_id ? "Move on calendar" : "Add to calendar"}</span>
          </button>
        )}
        {!t.event_id && !t.done && (
          <select value="" onChange={(e) => { if (e.target.value) attachEvent(t, e.target.value); }} data-testid={`job-assign-event-${t.id}`}
            title="Attach this job to an event already on the Calendar"
            className="h-9 px-2 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-xs font-bold bg-white shrink-0 max-w-[170px]">
            <option value="">Assign to event…</option>
            {events.filter((ev) => ev.id && !ev.archived).map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.name}{ev.event_date ? ` · ${ev.event_date}` : ""}</option>
            ))}
          </select>
        )}
        {t.event_id && (
          <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-50 text-[#007F3B] inline-flex items-center gap-1 shrink-0"
            data-testid={`job-on-calendar-${t.id}`}>
            <Check className="h-3 w-3" /> On calendar{events.find((ev) => ev.id === t.event_id)?.name ? `: ${events.find((ev) => ev.id === t.event_id).name}` : ""}
            <button onClick={() => attachEvent(t, "")} data-testid={`job-unlink-event-${t.id}`} title="Unlink from the calendar event" aria-label="Unlink from the calendar event"
              className="ml-0.5 rounded-full hover:bg-emerald-100 p-0.5 transition-colors">
              <X className="h-3 w-3" />
            </button>
          </span>
        )}
        {t.event_id && (
          <button onClick={() => navigate("/calendar")} data-testid={`job-open-calendar-${t.id}`} title="Open it on the Calendar to share or edit it again"
            className="h-9 px-3 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5 shrink-0">
            <CalendarPlus className="h-3.5 w-3.5" /> Open in calendar
          </button>
        )}
        <button onClick={() => startEdit(t)} data-testid={`job-edit-${t.id}`} title="Edit this job" aria-label="Edit this job"
          className="h-9 w-9 grid place-items-center rounded-lg border border-slate-300 text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors shrink-0">
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => remove(t)} data-testid={`job-delete-${t.id}`}
          className="h-9 w-9 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 transition-colors shrink-0">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="jobs-page">
      <AdminHeader title="To-Do" testid="jobs-title" backTestid="jobs-back" maxW="max-w-3xl"
        actions={outstanding.length > 0 ? (
          <span className="text-xs font-bold bg-[#ED8B00] text-white rounded-full px-2.5 py-1" data-testid="jobs-outstanding-count">
            {outstanding.length} to do
          </span>
        ) : null} />

      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
        {tasks !== null && tasks.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="jobs-stats">
            {statCard("overdue", "Overdue", overdueList.length, "bg-red-50 border-[#D5281B] text-[#D5281B]", "jobs-stat-overdue")}
            {statCard("today", "Due today", dueToday.length, "bg-amber-50 border-[#ED8B00] text-[#ED8B00]", "jobs-stat-today")}
            {statCard("week", "Due this week", dueWeek.length, "bg-sky-50 border-[#005EB8] text-[#005EB8]", "jobs-stat-week")}
            {statCard("all", "To do", outstanding.length, "bg-slate-100 border-slate-500 text-slate-700", "jobs-stat-total")}
            <div className="flex-1 min-w-[104px] rounded-xl border border-emerald-100 bg-emerald-50/60 p-3" data-testid="jobs-stat-done">
              <span className="block font-heading text-2xl font-bold leading-none text-[#007F3B]">{doneWeek.length}</span>
              <span className="block text-[10px] font-bold uppercase tracking-wide mt-1 text-[#007F3B]/80">Done this week</span>
            </div>
          </div>
        )}
        {filter !== "all" && (
          <p className="text-xs font-bold text-slate-500" data-testid="jobs-filter-note">
            Showing only: {{ overdue: "overdue", today: "due today", week: "due this week" }[filter]} —{" "}
            <button onClick={() => setFilter("all")} data-testid="jobs-filter-clear" className="text-[#005EB8] hover:underline">show everything</button>
          </p>
        )}
        {editing && (
          <div className="bg-sky-50 rounded-2xl border-2 border-[#005EB8] p-4 space-y-2.5" data-testid="job-edit-form">
            <p className="text-xs font-bold uppercase tracking-wide text-[#005EB8]">Editing job</p>
            <textarea rows={2} value={editing.title} onChange={(e) => setEditing((f) => ({ ...f, title: e.target.value }))}
              data-testid="job-edit-title"
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
              <select value={editing.kind} onChange={(e) => setEditing((f) => ({ ...f, kind: e.target.value }))}
                data-testid="job-edit-kind" className={`${inputCls} bg-white`}>
                {KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
              </select>
              <select value={editing.client_id} onChange={(e) => setEditing((f) => ({ ...f, client_id: e.target.value, contact_name: "", contact_email: "" }))}
                data-testid="job-edit-client" className={`${inputCls} bg-white`}>
                <option value="">No client</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={editing.contact_name} onChange={(e) => {
                const ct = (clients.find((c) => c.id === editing.client_id)?.contacts || []).find((x) => x.name === e.target.value);
                setEditing((f) => ({ ...f, contact_name: e.target.value, contact_email: ct?.email || "" }));
              }} data-testid="job-edit-contact" disabled={!editing.client_id} className={`${inputCls} bg-white`}>
                <option value="">{editing.client_id ? "Who to speak to…" : "Pick a company first"}</option>
                {(clients.find((c) => c.id === editing.client_id)?.contacts || []).map((ct, i) => (
                  <option key={ct.contact_id || i} value={ct.name}>{ct.name}{ct.role ? ` (${ct.role})` : ""}</option>
                ))}
              </select>
              <input type="date" value={editing.due_date} onChange={(e) => setEditing((f) => ({ ...f, due_date: e.target.value }))}
                data-testid="job-edit-date" className={`${inputCls} bg-white`} />
              <input type="time" value={editing.due_time} onChange={(e) => setEditing((f) => ({ ...f, due_time: e.target.value }))}
                data-testid="job-edit-time" className={`${inputCls} bg-white`} />
              <div className="flex gap-2">
                <button type="button" onClick={saveEdit} disabled={!editing.title.trim()} data-testid="job-edit-save"
                  className="flex-1 h-12 rounded-lg bg-[#005EB8] text-white text-sm font-bold hover:bg-[#004a93] disabled:opacity-50 transition-colors">Save changes</button>
                <button type="button" onClick={() => setEditing(null)} data-testid="job-edit-cancel"
                  className="h-12 px-4 rounded-lg border border-slate-300 text-sm font-bold text-slate-500 hover:border-slate-400 transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        )}
        <form onSubmit={add} className="bg-white rounded-2xl border border-slate-200 p-4 space-y-2.5" data-testid="job-add-form">
          <div className="flex items-start gap-2">
            <textarea required rows={2} placeholder="What needs doing? e.g. Raise invoice for Total Kombat — tap the mic to dictate it" value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="job-add-title"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <div className="pt-1.5">
              <VoiceButton testid="job-add-voice"
                onTranscript={(t) => setForm((f) => ({ ...f, title: (f.title ? f.title + " " : "") + t }))} />
            </div>
          </div>
          <p className="text-[11px] text-slate-400">Deadline is set automatically to one week from now — change the date or time if you need it sooner.</p>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
            <select value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
              data-testid="job-add-kind" className={`${inputCls} bg-white`}>
              {KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
            </select>
            <select value={form.client_id} onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value, contact_name: "", contact_email: "" }))}
              data-testid="job-add-client" className={`${inputCls} bg-white`}>
              <option value="">No client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={form.contact_name} onChange={(e) => {
              const ct = (clients.find((c) => c.id === form.client_id)?.contacts || []).find((x) => x.name === e.target.value);
              setForm((f) => ({ ...f, contact_name: e.target.value, contact_email: ct?.email || "" }));
            }} data-testid="job-add-contact" disabled={!form.client_id} className={`${inputCls} bg-white`}>
              <option value="">{form.client_id ? "Who to speak to…" : "Pick a company first"}</option>
              {(clients.find((c) => c.id === form.client_id)?.contacts || []).map((ct, i) => (
                <option key={ct.contact_id || i} value={ct.name}>{ct.name}{ct.role ? ` (${ct.role})` : ""}</option>
              ))}
            </select>
            <input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
              data-testid="job-add-date" title="Deadline date — auto-set to one week from now, adjust if needed" className={`${inputCls} bg-white`} />
            <input type="time" value={form.due_time} onChange={(e) => setForm((f) => ({ ...f, due_time: e.target.value }))}
              data-testid="job-add-time" title="Deadline time — overdue flags after this time on the day" className={`${inputCls} bg-white`} />
            <button type="submit" disabled={saving} data-testid="job-add-submit"
              className="h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-60 transition-colors text-white text-sm font-bold inline-flex items-center justify-center gap-1.5">
              <Plus className="h-4 w-4" /> Add to-do
            </button>
          </div>
        </form>

        {tasks === null ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : tasks.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="jobs-empty">
            <ListTodo className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Nothing on the list — add your first to-do above.</p>
          </div>
        ) : (
          <>
            <div className="space-y-2" data-testid="jobs-outstanding">
              {shown.length === 0 ? (
                <p className="text-xs text-slate-400 px-1 py-2" data-testid="jobs-filter-empty">Nothing in this view — nice.</p>
              ) : shown.map(renderTask)}
            </div>
            {done.length > 0 && (
              <div>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <PhoneCall className="h-3 w-3" /> Done ({done.length})
                </p>
                <div className="space-y-2" data-testid="jobs-done">{done.map(renderTask)}</div>
              </div>
            )}
          </>
        )}

        <div className="rounded-xl border border-[#00A499]/30 bg-teal-50 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="offers-link-reminder">
          <Package className="h-4 w-4 text-[#00A499] shrink-0" />
          <p className="text-xs text-slate-600 flex-1 min-w-[220px]">Your private share-out page — send it to clients:</p>
          <button type="button" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/people-we-trust`); toast.success("People we trust link copied — paste it to your client"); }} data-testid="trusted-copy-link"
            className="h-9 px-3 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-xs font-bold transition-colors">Copy people-we-trust link</button>
          <a href="/people-we-trust" target="_blank" rel="noreferrer" data-testid="trusted-open-link"
            className="h-9 px-3 rounded-lg border border-[#005EB8]/40 text-[#005EB8] hover:bg-blue-50 text-xs font-bold inline-flex items-center transition-colors">Open</a>
        </div>

        <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
          <Users className="h-3 w-3" /> Follow-ups you log against a client call or meeting land here automatically.
        </p>

        {askFor && (
          <AskQuestionsDialog task={askFor} clients={clients} events={events} reload={load} onClose={() => setAskFor(null)} />
        )}
        {viewForm && (
          <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setViewForm(null)} data-testid="qform-view-overlay">
            <div className="bg-white rounded-2xl max-w-lg w-full p-5 max-h-[85vh] overflow-y-auto space-y-3" onClick={(e) => e.stopPropagation()} data-testid="qform-view-dialog">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="font-heading text-lg font-bold text-[#0F2547]">{viewForm.title}</h2>
                  <p className="text-xs text-slate-500">{viewForm.client_name}{viewForm.contact_name ? ` · ${viewForm.contact_name}` : ""}{viewForm.event_name ? ` · ${viewForm.event_name}` : ""}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full shrink-0 ${viewForm.status === "answered" ? "bg-emerald-50 text-[#007F3B]" : "bg-amber-50 text-[#ED8B00]"}`} data-testid="qform-view-status">
                  {viewForm.status === "answered" ? "Answered" : "Awaiting reply"}
                </span>
              </div>
              {(viewForm.questions || []).map((q, i) => (
                <div key={i} className="rounded-lg bg-slate-50 border border-slate-200 p-2.5" data-testid={`qform-view-qa-${i}`}>
                  <p className="text-xs font-bold text-[#0F2547]">{q}</p>
                  <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">
                    {viewForm.status === "answered" ? (viewForm.answers?.[i]?.a || "—") : "Awaiting their reply…"}
                  </p>
                </div>
              ))}
              {viewForm.status === "answered" && !viewForm.event_id && (
                <div className="flex gap-2">
                  <select id="qform-link-event" data-testid="qform-link-event-select" defaultValue=""
                    className={`${inputCls} bg-white flex-1`}>
                    <option value="">Link these answers to an event…</option>
                    {(events || []).filter((e) => !viewForm.client_id || e.client_id === viewForm.client_id).map((e) => (
                      <option key={e.id} value={e.id}>{e.name}{e.event_date ? ` — ${e.event_date}` : ""}</option>
                    ))}
                  </select>
                  <button onClick={() => linkFormEvent(viewForm, document.getElementById("qform-link-event").value)} data-testid="qform-link-event-btn"
                    className="h-12 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-xs font-bold shrink-0">Link</button>
                </div>
              )}
              {viewForm.event_id && (
                <p className="text-[11px] text-emerald-600 font-semibold" data-testid="qform-linked-note">
                  Linked to {viewForm.event_name || "an event"} — the answers are in that event's notes on the calendar.
                </p>
              )}
              {viewForm.status === "answered" && (
                <button
                  onClick={() => {
                    setAskFor({ id: "", client_id: viewForm.client_id || "", event_id: viewForm.event_id || "",
                                contact_name: viewForm.contact_name || "", contact_email: viewForm.contact_email || "" });
                    setViewForm(null);
                  }}
                  data-testid="qform-ask-more-btn"
                  className="w-full h-11 rounded-lg border-2 border-violet-500 text-violet-700 hover:bg-violet-50 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors">
                  <HelpCircle className="h-4 w-4" /> Ask more questions
                </button>
              )}
              <button onClick={() => setViewForm(null)} data-testid="qform-view-close"
                className="w-full h-11 rounded-lg border border-slate-300 text-sm font-bold text-slate-500 hover:border-slate-400 transition-colors inline-flex items-center justify-center gap-1.5">
                <X className="h-4 w-4" /> Close
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
